import PropTypes from 'prop-types';
import React from 'react';
import moment from 'moment';
import { connect } from 'react-redux';
import { Dialog, IconButton, Button } from 'react-toolbox';
import { ifiLrrpCreate, updatePhaseTask } from 'actionCreators';
import PhaseType from 'types/phase';
import modalStyles from 'modal.scss';
import S from './ifiLrrpModal.scss';

const STATUS_OPTIONS = ['yes', 'no', 'na'];
const LEAD_TEST_RESULT_OPTIONS = ['', 'negative', 'positive', 'presumed'];
const CLEANING_METHOD_OPTIONS = [
  { value: '', label: 'Select...' },
  { value: 'cleaning_verification', label: 'Cleaning Verification' },
  { value: 'dust_clearance', label: 'Dust Clearance' },
];
// These exact strings are matched verbatim by app/views/pdfs/ifi_lrrp.html.erb — keep both in sync.
const WORK_AREA_CONTAINMENT_ITEMS = [
  { group: 'INTERIOR', value: 'All objects in the work area were removed or covered (interiors).' },
  { group: 'INTERIOR', value: 'HVAC ducts in the work area were closed and covered (interiors).' },
  { group: 'INTERIOR', value: 'Windows and doors in the work area were closed and sealed (interiors).' },
  { group: 'INTERIOR', value: 'Floor surfaces covered by plastic extending 6 feet from work area (interiors).' },
  { group: 'INTERIOR', value: 'Doors in the work area were closed and sealed (interiors).' },
  {
    group: 'INTERIOR',
    value: 'Doors that must be used in the work area were covered to allow passage but prevent spread of dust (interiors).',
  },
  { group: 'INTERIOR', value: 'Floors in the work area were covered with taped-down plastic (interiors).' },
  { group: 'EXTERIOR', value: 'Windows in and within 20 feet of the work area were closed (exteriors).' },
  { group: 'EXTERIOR', value: 'Doors in and within 20 feet of the work area were closed and sealed (exteriors).' },
  {
    group: 'EXTERIOR',
    value: 'Ground was covered by plastic extending 10 feet from work area - plastic anchored to building and weighed down by heavy objects (exteriors).',
  },
  {
    group: 'EXTERIOR',
    value: 'If necessary, vertical containment was installed if property line prevents 10 feet of plastic ground cover, or if necessary to prevent migration of dust and debris to adjacent property (exteriors).',
  },
];
const NOTIFICATION_ACKNOWLEDGMENT_OPTIONS = [
  'Signed and dated acknowledgements of receipt of the Renovate Right pamphlet from, as ' +
    'applicable: owners and, if not owner-occupied, adult occupants of dwelling units, owners ' +
    'of multi-unit housing for renovations in common areas; and owners and adult ' +
    'representatives of child-occupied facilities are on file.',
  'Certificates of mailing of the Renovate Right pamphlet to the following, as applicable: ' +
    'owners and, if not owner-occupied, adult occupants of dwelling units; owners of ' +
    'multi-unit housing for renovations in common areas; and owners and adult representatives ' +
    'of child-occupied facilities are on file.',
  'Certificates of attempted delivery of the Renovate Right pamphlet to adult occupants of ' +
    'dwelling units or adult representatives of child-occupied facilities are on file.',
];

const formatAddress = (phase) => {
  const parts = [phase.street_address, phase.city, phase.state, phase.zip_code];
  return parts.filter(Boolean).join(', ');
};

class IfiLrrpModal extends React.Component {
  constructor(props) {
    super(props);
    this.state = this.defaultState();
  }

  componentDidUpdate(prevProps, prevState) {
    const justOpened = this.props.active && !prevProps.active;
    if (justOpened) {
      // Reset form state each time the modal opens.
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState(this.defaultState());
      return;
    }

    const wasExtended = this.requiresExtendedLrrpPages(prevState);
    const isExtended = this.requiresExtendedLrrpPages(this.state);
    if (wasExtended && !isExtended) {
      // A corrected lead status/test result turned off pages 3-5: clear their
      // state so stale compliance data can't be submitted for a phase that no
      // longer requires it.
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState(this.extendedPagesDefaultState());
    }
  }

  extractErrorMessage = (error) => {
    const messageFromErrorsArray = (((error || {}).errors || {}).messages || [])[0];
    const fullMessages = (((error || {}).errors || {}).full_messages || [])[0];
    const fieldErrors = ((error || {}).errors || {});
    const firstFieldError = Object.keys(fieldErrors).reduce((result, field) => {
      if (result) return result;
      const fieldValue = fieldErrors[field];
      if (Array.isArray(fieldValue) && fieldValue.length > 0) {
        return `${field} ${fieldValue[0]}`;
      }
      if (typeof fieldValue === 'string' && fieldValue.trim()) {
        return `${field} ${fieldValue}`;
      }
      return result;
    }, '');
    const topLevelMessage = (error || {}).message;
    return messageFromErrorsArray || fullMessages || firstFieldError || topLevelMessage || '';
  }

  defaultState = () => {
    const { phase, userName, userLeadCertNumber } = this.props;
    const phaseAddress = formatAddress(phase);
    const installerCert = phase.installer_certification || {};
    return {
      loading: false,
      errorMessage: '',
      currentPage: 1,
      customer_name: phase.customer_name || '',
      contract_number: phase.project_number || '',
      customer_address: phaseAddress,
      installation_address: phaseAddress,
      installer_firm_name: installerCert.name || userName || '',
      installer_firm_certification_number: installerCert.installer_firm_certification_number || '',
      assigned_renovator_name: installerCert.name || userName || '',
      assigned_renovator_certification_number:
        installerCert.assigned_renovator_certification_number || userLeadCertNumber || '',
      renovation_project_description: '',
      installer_name_and_title: installerCert.name || userName || '',
      installer_certification_date: moment().format('YYYY-MM-DD'),
      installer_signature: installerCert.name || userName || '',
      written_determination_status: 'no',
      lead_testing_status: 'no',
      certified_renovator_name: installerCert.name || userName || '',
      certified_renovator_certification_number:
        installerCert.assigned_renovator_certification_number || userLeadCertNumber || '',
      was_area_presumed_lead_status: 'no',
      disturbance_below_threshold_status: 'no',
      notification_records_status: 'na',
      warning_signs_posted_status: 'yes',
      work_area_containment_items: [],
      prohibited_practices_status: 'yes',
      waste_contained_status: 'yes',
      worksite_cleaned_status: 'yes',
      post_renovation_cleaning_method: '',
      wet_cloths_used: '',
      dry_cloths_used: '',
      lead_response_explanation: '',
      post_renovation_cleaning_verification: '',
      emergency_renovation_description: '',
      lead_tests: [this.blankLeadTest()],
      dust_sampling_technician_names_text: '',
      workers_trained_names_text: '',
      workers_trained_activities_text: '',
      notification_records_acknowledgment: '',
    };
  }

  // Only the fields backing pages 3-5, which exist solely on the presumed/positive path.
  extendedPagesDefaultState = () => {
    const {
      notification_records_status,
      notification_records_acknowledgment,
      warning_signs_posted_status,
      work_area_containment_items,
      prohibited_practices_status,
      waste_contained_status,
      worksite_cleaned_status,
      post_renovation_cleaning_method,
      wet_cloths_used,
      dry_cloths_used,
      lead_response_explanation,
      post_renovation_cleaning_verification,
      dust_sampling_technician_names_text,
      workers_trained_names_text,
      workers_trained_activities_text,
      emergency_renovation_description,
    } = this.defaultState();

    return {
      notification_records_status,
      notification_records_acknowledgment,
      warning_signs_posted_status,
      work_area_containment_items,
      prohibited_practices_status,
      waste_contained_status,
      worksite_cleaned_status,
      post_renovation_cleaning_method,
      wet_cloths_used,
      dry_cloths_used,
      lead_response_explanation,
      post_renovation_cleaning_verification,
      dust_sampling_technician_names_text,
      workers_trained_names_text,
      workers_trained_activities_text,
      emergency_renovation_description,
    };
  }

  handleChange = field => (e) => {
    const value = e.target.value;

    if (field === 'lead_testing_status') {
      this.setState({
        lead_testing_status: value,
        lead_tests: value === 'yes' && this.state.lead_tests.length > 0 ?
          this.state.lead_tests :
          [this.blankLeadTest()],
      });
      return;
    }

    if (field === 'notification_records_status') {
      this.setState({
        notification_records_status: value,
        notification_records_acknowledgment: value === 'yes' ?
          this.state.notification_records_acknowledgment :
          '',
      });
      return;
    }

    if (field === 'post_renovation_cleaning_method') {
      this.setState({
        post_renovation_cleaning_method: value,
        post_renovation_cleaning_verification: value === 'cleaning_verification' ?
          this.state.post_renovation_cleaning_verification :
          '',
        dust_sampling_technician_names_text: value === 'dust_clearance' ?
          this.state.dust_sampling_technician_names_text :
          '',
      });
      return;
    }

    this.setState({ [field]: value });
  }

  handleContainmentItemToggle = value => (e) => {
    const { checked } = e.target;
    const items = this.state.work_area_containment_items;
    this.setState({
      work_area_containment_items: checked ?
        [...items, value] :
        items.filter(item => item !== value),
    });
  }

  leadTestIdCounter = 0

  blankLeadTest = () => {
    this.leadTestIdCounter += 1;
    return {
      id: this.leadTestIdCounter,
      result: '',
      test_kit_manufacturer_model: '',
      component_location_tested: '',
      test_kit_lot_number: '',
    };
  }

  handleLeadTestChange = (index, field) => (e) => {
    const value = e.target.value;

    const leadTests = this.state.lead_tests.map((test, i) => {
      if (i !== index) return test;
      if (field === 'result' && !['positive', 'negative'].includes(value)) {
        return {
          ...test,
          result: value,
          test_kit_manufacturer_model: '',
          component_location_tested: '',
          test_kit_lot_number: '',
        };
      }
      return { ...test, [field]: value };
    });

    this.setState({ lead_tests: leadTests });
  }

  addLeadTest = () => {
    this.setState({ lead_tests: [...this.state.lead_tests, this.blankLeadTest()] });
  }

  removeLeadTest = (index) => {
    const leadTests = this.state.lead_tests.filter((_test, i) => i !== index);
    this.setState({ lead_tests: leadTests.length > 0 ? leadTests : [this.blankLeadTest()] });
  }

  handleClose = () => {
    if (!this.state.loading) this.props.onClose();
  }

  parseCommaSeparated = value =>
    value
      .split(',')
      .map(item => item.trim())
      .filter(Boolean)

  leadPositiveOrPresumed = (state = this.state) => {
    const { was_area_presumed_lead_status, lead_tests } = state;
    if (was_area_presumed_lead_status === 'yes') return true;

    return lead_tests.some((test) => {
      const normalizedResult = (test.result || '').toLowerCase();
      return normalizedResult.includes('positive') || normalizedResult.includes('presum');
    });
  }

  requiresExtendedLrrpPages = (state = this.state) => {
    // "Detail" (Assessment) only requires pages 1-2 and 6.
    if (this.props.phase.type === 'Assessment') return false;
    return this.leadPositiveOrPresumed(state);
  }

  isLeadTestPositiveOrNegative = (test) => {
    const normalizedResult = (test.result || '').toLowerCase();
    return normalizedResult.includes('positive') || normalizedResult.includes('negative');
  }

  visiblePages = () => {
    const pages = [1, 2];
    if (this.requiresExtendedLrrpPages()) pages.push(3, 4, 5);
    pages.push(6);
    return pages;
  }

  currentVisiblePage = () => {
    const pages = this.visiblePages();
    const { currentPage } = this.state;

    if (pages.includes(currentPage)) return currentPage;

    // If extended pages are no longer required while user is on 3-5,
    // return to page 2 instead of jumping to the last page.
    if (
      !this.requiresExtendedLrrpPages() &&
      currentPage >= 3 &&
      currentPage <= 5
    ) {
      return 2;
    }

    return pages[pages.length - 1];
  }

  goToNextPage = () => {
    const pages = this.visiblePages();
    const currentPage = this.currentVisiblePage();
    const currentIndex = pages.indexOf(currentPage);
    const nextPage = pages[currentIndex + 1];
    if (nextPage) this.setState({ currentPage: nextPage });
  }

  goToPreviousPage = () => {
    const pages = this.visiblePages();
    const currentPage = this.currentVisiblePage();
    const currentIndex = pages.indexOf(currentPage);
    const previousPage = pages[currentIndex - 1];
    if (previousPage) this.setState({ currentPage: previousPage });
  }

  validateForm = () => {
    const requiredFields = [
      'installer_name_and_title',
      'installer_certification_date',
      'installer_signature',
    ];

    const missingField = requiredFields.find(field => !this.state[field]);
    if (missingField) {
      this.setState({ errorMessage: 'Installer name/title, certification date, and signature are required.' });
      return false;
    }

    const workersTrainedActivities = this.parseCommaSeparated(
      this.state.workers_trained_activities_text,
    );
    const workersTrainedNames = this.parseCommaSeparated(
      this.state.workers_trained_names_text,
    );
    if (workersTrainedActivities.length > 0 && workersTrainedNames.length === 0) {
      this.setState({ errorMessage: 'Worker names are required when trained activities are listed.' });
      return false;
    }
    if (workersTrainedNames.length > 0 && workersTrainedActivities.length === 0) {
      this.setState({ errorMessage: 'Trained activities are required when worker names are listed.' });
      return false;
    }

    if (this.state.lead_testing_status === 'yes') {
      const hasLeadTestResult = this.state.lead_tests.some(test => test.result);
      if (!hasLeadTestResult) {
        this.setState({ errorMessage: 'At least one lead test result is required when lead testing is marked YES.' });
        return false;
      }

      const missingTestKitInfo = this.state.lead_tests.some((test) => {
        if (!this.isLeadTestPositiveOrNegative(test)) return false;
        return [
          'test_kit_manufacturer_model',
          'component_location_tested',
          'test_kit_lot_number',
        ].some(field => !test[field]);
      });
      if (missingTestKitInfo) {
        this.setState({
          errorMessage: 'Test kit manufacturer/model, component location tested, and lot number are required for positive/negative results.',
        });
        return false;
      }
    }

    if (this.requiresExtendedLrrpPages()) {
      if (!this.state.lead_response_explanation.trim()) {
        this.setState({ errorMessage: 'Lead response explanation is required when lead is positive or presumed.' });
        return false;
      }

      if (this.state.notification_records_status === 'na') {
        this.setState({ errorMessage: 'Notification records status must be completed when lead is positive or presumed.' });
        return false;
      }

      if (
        this.state.notification_records_status === 'yes' &&
        !this.state.notification_records_acknowledgment
      ) {
        this.setState({
          errorMessage: 'A notification records acknowledgment is required when notification records are marked YES.',
        });
        return false;
      }

      const requiredStatuses = [
        'warning_signs_posted_status',
        'prohibited_practices_status',
        'waste_contained_status',
        'worksite_cleaned_status',
      ];
      const missingStatus = requiredStatuses.find(field => this.state[field] === 'na');
      if (missingStatus) {
        this.setState({
          errorMessage: 'All required status fields on pages 3-5 must be answered when lead is positive or presumed.',
        });
        return false;
      }

      if (!this.state.post_renovation_cleaning_method) {
        this.setState({
          errorMessage: 'Post renovation cleaning method is required when lead is positive or presumed.',
        });
        return false;
      }

      if (
        this.state.post_renovation_cleaning_method === 'cleaning_verification' &&
        !this.state.post_renovation_cleaning_verification.trim()
      ) {
        this.setState({
          errorMessage: 'Cleaning verification result is required when lead is positive or presumed.',
        });
        return false;
      }

      if (
        this.state.post_renovation_cleaning_method === 'dust_clearance' &&
        !String(this.state.dust_sampling_technician_names_text || '').trim()
      ) {
        this.setState({
          errorMessage: 'Dust sampling technician names are required when the cleaning method is Dust Clearance.',
        });
        return false;
      }

      const wetClothsUsed = String(this.state.wet_cloths_used || '').trim();
      const dryClothsUsed = String(this.state.dry_cloths_used || '').trim();
      if (!wetClothsUsed || !dryClothsUsed) {
        this.setState({
          errorMessage: 'Number of wet and dry cloths used is required when lead is positive or presumed.',
        });
        return false;
      }

      if (!/^\d+$/.test(wetClothsUsed) || !/^\d+$/.test(dryClothsUsed)) {
        this.setState({ errorMessage: 'Wet and dry cloth values must be whole numbers.' });
        return false;
      }

      // Keep non-red optional fields optional for the presumed/positive path.
    }

    return true;
  }

  buildPayload = () => {
    const leadTestInfo = this.state.lead_testing_status === 'yes' ?
      this.state.lead_tests
        .filter(test => test.result)
        .map(test => ({
          result: test.result,
          test_kit_manufacturer_model: test.test_kit_manufacturer_model,
          component_location_tested: test.component_location_tested,
          test_kit_lot_number: test.test_kit_lot_number,
        })) :
      [];
    const dustSamplingTechnicians = this.parseCommaSeparated(
      this.state.dust_sampling_technician_names_text,
    );
    const workersTrainedNames = this.parseCommaSeparated(this.state.workers_trained_names_text);
    const workersTrainedActivities = this.parseCommaSeparated(
      this.state.workers_trained_activities_text,
    );
    const notificationRecords = this.state.notification_records_acknowledgment ?
      { acknowledgment: this.state.notification_records_acknowledgment } :
      {};

    return {
      phase_id: this.props.phase.id,
      customer_name: this.state.customer_name,
      contract_number: this.state.contract_number,
      customer_address: this.state.customer_address,
      installation_address: this.state.installation_address,
      installer_firm_name: this.state.installer_firm_name,
      installer_firm_certification_number: this.state.installer_firm_certification_number,
      assigned_renovator_name: this.state.assigned_renovator_name,
      assigned_renovator_certification_number: this.state.assigned_renovator_certification_number,
      renovation_project_description: this.state.renovation_project_description,
      installer_name_and_title: this.state.installer_name_and_title,
      installer_certification_date: this.state.installer_certification_date,
      installer_signature: this.state.installer_signature,
      written_determination_status: this.state.written_determination_status,
      lead_testing_status: this.state.lead_testing_status,
      certified_renovator_name: this.state.certified_renovator_name,
      certified_renovator_certification_number: this.state.certified_renovator_certification_number,
      was_area_presumed_lead_status: this.state.was_area_presumed_lead_status,
      disturbance_below_threshold_status: this.state.disturbance_below_threshold_status,
      notification_records_status: this.state.notification_records_status,
      warning_signs_posted_status: this.state.warning_signs_posted_status,
      prohibited_practices_status: this.state.prohibited_practices_status,
      waste_contained_status: this.state.waste_contained_status,
      worksite_cleaned_status: this.state.worksite_cleaned_status,
      post_renovation_cleaning_method: this.state.post_renovation_cleaning_method,
      wet_cloths_used: this.state.wet_cloths_used,
      dry_cloths_used: this.state.dry_cloths_used,
      lead_response_explanation: this.state.lead_response_explanation,
      post_renovation_cleaning_verification: this.state.post_renovation_cleaning_verification,
      emergency_renovation_description: this.state.emergency_renovation_description,
      lead_test_info: leadTestInfo,
      dust_sampling_technician_names: dustSamplingTechnicians,
      workers_trained_names: workersTrainedNames,
      workers_trained_activities: workersTrainedActivities,
      work_area_containment_items: this.state.work_area_containment_items,
      notification_records: notificationRecords,
      metadata: {},
    };
  }

  buildTaskCompletionPayload = () => {
    if (this.props.taskSlug !== 'lead_assessment') {
      return { task: { status: 'complete' } };
    }

    const disruptingLead = this.leadPositiveOrPresumed() ? 'yes' : 'no';
    const commNote = String(
      this.state.lead_response_explanation ||
      this.state.renovation_project_description ||
      'IFI LRRP completed',
    ).trim();

    return {
      task: { status: 'complete' },
      data: {
        disrupting_lead: disruptingLead,
        comm_note: commNote,
      },
    };
  }

  submit = () => {
    if (!this.validateForm()) return;

    this.setState({ loading: true, errorMessage: '' });
    this.props.ifiLrrpCreate(this.buildPayload())
      .then(() => {
        const { phase, taskSlug } = this.props;
        if (!taskSlug) return null;
        return this.props.updatePhaseTask(
          phase.id,
          taskSlug,
          this.buildTaskCompletionPayload(),
        ).then((action) => {
          if (action && action.type === 'UPDATE_PHASE_TASK_FAILURE') {
            const error = new Error('Unable to mark phase task as complete.');
            error.action = action;
            throw error;
          }
          return action;
        });
      })
      .then(() => {
        this.setState({ loading: false, errorMessage: '' });
        this.props.onClose();
        if (typeof window !== 'undefined' && window.location && typeof window.location.reload === 'function') {
          window.location.reload();
        }
      })
      .catch((error) => {
        const serverError = this.extractErrorMessage(error);
        const taskCompletionError = error && error.message === 'Unable to mark phase task as complete.';
        this.setState({
          loading: false,
          errorMessage: taskCompletionError ?
            'IFI LRRP was created, but we could not mark the task complete. Please refresh the page.' :
            (serverError || 'Unable to create IFI LRRP. Please review the form and retry.'),
        });
      });
  }

  renderStatusSelect = (field, label) => (
    <div className={S.row}>
      <label className={S.label} htmlFor={field}>{label}</label>
      <select
        id={field}
        className={S.select}
        value={this.state[field]}
        onChange={this.handleChange(field)}
      >
        {STATUS_OPTIONS.map(option => (
          <option key={option} value={option}>{option.toUpperCase()}</option>
        ))}
      </select>
    </div>
  )

  renderCleaningMethodSelect = () => (
    <div className={S.row}>
      <label className={S.label} htmlFor="post_renovation_cleaning_method">Post Renovation Cleaning Method</label>
      <select
        id="post_renovation_cleaning_method"
        className={S.select}
        value={this.state.post_renovation_cleaning_method}
        onChange={this.handleChange('post_renovation_cleaning_method')}
      >
        {CLEANING_METHOD_OPTIONS.map(option => (
          <option key={option.value || 'blank'} value={option.value}>{option.label}</option>
        ))}
      </select>
    </div>
  )

  renderNotificationAcknowledgmentRadios = () => (
    <div className={S.row}>
      <span className={S.label}>Please acknowledge one of the following:</span>
      {NOTIFICATION_ACKNOWLEDGMENT_OPTIONS.map((option, index) => {
        const id = `notification_records_acknowledgment_${index}`;
        return (
          <label key={option} className={S.checkboxRow} htmlFor={id}>
            <input
              id={id}
              type="radio"
              name="notification_records_acknowledgment"
              value={option}
              checked={this.state.notification_records_acknowledgment === option}
              onChange={this.handleChange('notification_records_acknowledgment')}
            />
            {option}
          </label>
        );
      })}
    </div>
  )

  renderContainmentChecklist = () => (
    <div className={S.row}>
      <span className={S.label}>
        Work area contained to prevent spread of dust and debris? (Check all that apply)
      </span>
      {['INTERIOR', 'EXTERIOR'].map(group => (
        <div key={group} className={S.checklistGroup}>
          <span className={S.checklistGroupLabel}>{group}</span>
          {WORK_AREA_CONTAINMENT_ITEMS.filter(item => item.group === group).map((item, index) => {
            const id = `work_area_containment_item_${group}_${index}`;
            return (
              <label key={item.value} className={S.checkboxRow} htmlFor={id}>
                <input
                  id={id}
                  type="checkbox"
                  checked={this.state.work_area_containment_items.includes(item.value)}
                  onChange={this.handleContainmentItemToggle(item.value)}
                />
                {item.value}
              </label>
            );
          })}
        </div>
      ))}
    </div>
  )

  renderPageFields = (currentPage) => {
    if (currentPage === 1) {
      return (<div>
        <div className={S.row}>
          <label className={S.label} htmlFor="customer_name">Customer Name</label>
          <input
            id="customer_name" className={S.input} value={this.state.customer_name}
            onChange={this.handleChange('customer_name')}
          />
        </div>
        <div className={S.row}>
          <label className={S.label} htmlFor="contract_number">Contract Number</label>
          <input
            id="contract_number" className={S.input} value={this.state.contract_number}
            onChange={this.handleChange('contract_number')}
          />
        </div>
        <div className={S.row}>
          <label className={S.label} htmlFor="customer_address">Customer Address</label>
          <input
            id="customer_address" className={S.input} value={this.state.customer_address}
            onChange={this.handleChange('customer_address')}
          />
        </div>
        <div className={S.row}>
          <label className={S.label} htmlFor="installation_address">Installation Address</label>
          <input
            id="installation_address" className={S.input} value={this.state.installation_address}
            onChange={this.handleChange('installation_address')}
          />
        </div>
        <div className={S.row}>
          <label className={S.label} htmlFor="installer_firm_name">Installer Firm Name</label>
          <input
            id="installer_firm_name"
            className={S.input}
            value={this.state.installer_firm_name}
            onChange={this.handleChange('installer_firm_name')}
          />
        </div>
        <div className={S.row}>
          <label className={S.label} htmlFor="installer_firm_certification_number">
            Installer Firm Certification Number
          </label>
          <input
            id="installer_firm_certification_number"
            className={S.input}
            value={this.state.installer_firm_certification_number}
            onChange={this.handleChange('installer_firm_certification_number')}
          />
        </div>
        <div className={S.row}>
          <label className={S.label} htmlFor="assigned_renovator_name">Assigned Renovator Name</label>
          <input
            id="assigned_renovator_name"
            className={S.input}
            value={this.state.assigned_renovator_name}
            onChange={this.handleChange('assigned_renovator_name')}
          />
        </div>
        <div className={S.row}>
          <label className={S.label} htmlFor="assigned_renovator_certification_number">
            Assigned Renovator Certification Number
          </label>
          <input
            id="assigned_renovator_certification_number"
            className={S.input}
            value={this.state.assigned_renovator_certification_number}
            onChange={this.handleChange('assigned_renovator_certification_number')}
          />
        </div>
        <div className={S.row}>
          <label className={S.label} htmlFor="renovation_project_description">Project Description</label>
          <textarea
            id="renovation_project_description" className={S.textarea}
            value={this.state.renovation_project_description}
            onChange={this.handleChange('renovation_project_description')}
          />
        </div>
      </div>);
    }

    if (currentPage === 2) {
      return (<div>
        {this.renderStatusSelect('written_determination_status', 'Written Determination')}
        <div className={S.row}>
          <label className={S.label} htmlFor="certified_renovator_name">Certified Renovator Name</label>
          <input
            id="certified_renovator_name"
            className={S.input}
            value={this.state.certified_renovator_name}
            onChange={this.handleChange('certified_renovator_name')}
          />
        </div>
        <div className={S.row}>
          <label className={S.label} htmlFor="certified_renovator_certification_number">
            Certified Renovator Certification Number
          </label>
          <input
            id="certified_renovator_certification_number"
            className={S.input}
            value={this.state.certified_renovator_certification_number}
            onChange={this.handleChange('certified_renovator_certification_number')}
          />
        </div>
        {this.renderStatusSelect('was_area_presumed_lead_status', 'Area Presumed Lead')}
        {this.renderStatusSelect('disturbance_below_threshold_status', 'Not Disrupting 6 Sq Ft Interior / 20 Sq Ft Exterior')}
        {this.renderStatusSelect('lead_testing_status', 'Lead Testing')}
        {this.state.lead_testing_status === 'yes' && <div>
          {this.state.lead_tests.map((test, index) => (
            <div key={test.id} className={S.leadTestGroup}>
              <div className={S.leadTestHeader}>
                <span>Lead Test {index + 1}</span>
                {this.state.lead_tests.length > 1 && (
                  <Button
                    label="Remove"
                    flat
                    onClick={() => this.removeLeadTest(index)}
                  />
                )}
              </div>
              <div className={S.row}>
                <label className={S.label} htmlFor={`lead_test_result_${index}`}>Lead Test Result</label>
                <select
                  id={`lead_test_result_${index}`} className={S.select} value={test.result}
                  onChange={this.handleLeadTestChange(index, 'result')}
                  required
                  aria-required="true"
                >
                  {LEAD_TEST_RESULT_OPTIONS.map(option => (
                    <option key={option || 'blank'} value={option}>
                      {option ? option.toUpperCase() : 'NONE'}
                    </option>
                  ))}
                </select>
              </div>
              {this.isLeadTestPositiveOrNegative(test) && <div>
                <div className={S.row}>
                  <label className={S.label} htmlFor={`lead_test_kit_manufacturer_model_${index}`}>
                    Test Kit Manufacturer / Model
                  </label>
                  <input
                    id={`lead_test_kit_manufacturer_model_${index}`} className={S.input}
                    value={test.test_kit_manufacturer_model}
                    onChange={this.handleLeadTestChange(index, 'test_kit_manufacturer_model')}
                    required
                    aria-required="true"
                  />
                </div>
                <div className={S.row}>
                  <label className={S.label} htmlFor={`lead_test_component_location_tested_${index}`}>
                    Component Location Tested
                  </label>
                  <input
                    id={`lead_test_component_location_tested_${index}`} className={S.input}
                    value={test.component_location_tested}
                    onChange={this.handleLeadTestChange(index, 'component_location_tested')}
                    required
                    aria-required="true"
                  />
                </div>
                <div className={S.row}>
                  <label className={S.label} htmlFor={`lead_test_kit_lot_number_${index}`}>Test Kit Lot Number</label>
                  <input
                    id={`lead_test_kit_lot_number_${index}`} className={S.input}
                    value={test.test_kit_lot_number}
                    onChange={this.handleLeadTestChange(index, 'test_kit_lot_number')}
                    required
                    aria-required="true"
                  />
                </div>
              </div>}
            </div>
          ))}
          <Button label="Add Another Lead Test" flat onClick={this.addLeadTest} className={S.addLeadTest} />
        </div>}
      </div>);
    }

    if (currentPage === 3) {
      return (<div>
        {this.renderStatusSelect('notification_records_status', 'Notification Records')}
        {this.state.notification_records_status === 'yes' && this.renderNotificationAcknowledgmentRadios()}
        <div className={S.row}>
          <label className={S.label} htmlFor="lead_response_explanation">Lead Response Explanation</label>
          <textarea
            id="lead_response_explanation" className={S.textarea}
            value={this.state.lead_response_explanation}
            onChange={this.handleChange('lead_response_explanation')}
          />
        </div>
      </div>);
    }

    if (currentPage === 4) {
      return (<div>
        {this.renderStatusSelect('warning_signs_posted_status', 'Warning Signs Posted')}
        {this.renderContainmentChecklist()}
        {this.renderStatusSelect('prohibited_practices_status', 'Prohibited Practices')}
        {this.renderStatusSelect('waste_contained_status', 'Waste Contained')}
        {this.renderStatusSelect('worksite_cleaned_status', 'Worksite Cleaned')}
        {this.renderCleaningMethodSelect()}
        {this.state.post_renovation_cleaning_method === 'cleaning_verification' && <div className={S.row}>
          <label className={S.label} htmlFor="post_renovation_cleaning_verification">
            Cleaning Verification Result
          </label>
          <textarea
            id="post_renovation_cleaning_verification" className={S.textarea}
            value={this.state.post_renovation_cleaning_verification}
            onChange={this.handleChange('post_renovation_cleaning_verification')}
          />
        </div>}
        {this.state.post_renovation_cleaning_method === 'dust_clearance' && <div className={S.row}>
          <label className={S.label} htmlFor="dust_sampling_technician_names_text">
            Dust Sampling Technician Names (comma-separated)
          </label>
          <input
            id="dust_sampling_technician_names_text" className={S.input}
            value={this.state.dust_sampling_technician_names_text}
            onChange={this.handleChange('dust_sampling_technician_names_text')}
          />
        </div>}
        <div className={S.row}>
          <label className={S.label} htmlFor="wet_cloths_used">Number of Wet Cloths Used</label>
          <input
            id="wet_cloths_used"
            className={S.input}
            value={this.state.wet_cloths_used}
            onChange={this.handleChange('wet_cloths_used')}
          />
        </div>
        <div className={S.row}>
          <label className={S.label} htmlFor="dry_cloths_used">Number of Dry Cloths Used</label>
          <input
            id="dry_cloths_used"
            className={S.input}
            value={this.state.dry_cloths_used}
            onChange={this.handleChange('dry_cloths_used')}
          />
        </div>
      </div>);
    }

    if (currentPage === 5) {
      return (<div>
        <div className={S.row}>
          <label className={S.label} htmlFor="workers_trained_activities_text">
            Workers Trained Activities (comma-separated)
          </label>
          <input
            id="workers_trained_activities_text" className={S.input}
            value={this.state.workers_trained_activities_text}
            onChange={this.handleChange('workers_trained_activities_text')}
          />
        </div>
        <div className={S.row}>
          <label className={S.label} htmlFor="workers_trained_names_text">
            Workers Trained Names (comma-separated)
          </label>
          <input
            id="workers_trained_names_text" className={S.input}
            value={this.state.workers_trained_names_text}
            onChange={this.handleChange('workers_trained_names_text')}
          />
        </div>
        <div className={S.row}>
          <label className={S.label} htmlFor="emergency_renovation_description">Emergency Renovation Description</label>
          <textarea
            id="emergency_renovation_description" className={S.textarea}
            value={this.state.emergency_renovation_description}
            onChange={this.handleChange('emergency_renovation_description')}
          />
        </div>
      </div>);
    }

    return (<div>
      <div className={S.row}>
        <label className={S.label} htmlFor="installer_name_and_title">Installer Name and Title *</label>
        <input
          id="installer_name_and_title" className={S.input} required aria-required="true"
          value={this.state.installer_name_and_title}
          onChange={this.handleChange('installer_name_and_title')}
        />
      </div>
      <div className={S.row}>
        <label className={S.label} htmlFor="installer_certification_date">Installer Certification Date *</label>
        <input
          id="installer_certification_date" className={S.input} type="date" required aria-required="true"
          value={this.state.installer_certification_date}
          onChange={this.handleChange('installer_certification_date')}
        />
      </div>
      <div className={S.row}>
        <label className={S.label} htmlFor="installer_signature">Installer Signature *</label>
        <input
          id="installer_signature" className={S.input} required aria-required="true"
          value={this.state.installer_signature}
          onChange={this.handleChange('installer_signature')}
        />
      </div>
    </div>);
  }

  render = () => {
    const visiblePages = this.visiblePages();
    const currentPage = this.currentVisiblePage();
    const currentIndex = visiblePages.indexOf(currentPage);
    const atFirstPage = currentIndex === 0;
    const atLastPage = currentIndex === visiblePages.length - 1;

    return (
      <Dialog
        active={this.props.active}
        className={modalStyles.modal}
        onEscKeyDown={this.handleClose}
        onOverlayClick={this.handleClose}
      >
        <IconButton icon="close" className={modalStyles.close} onClick={this.handleClose} />
        <h5>Create IFI LRRP</h5>
        <p>This action creates the IFI LRRP record and uploads the generated LRRP document.</p>
        <div className={S.pageHeader}>
          <span>Page {currentIndex + 1} of {visiblePages.length}</span>
          <span className={S.pageSeparator}>|</span>
          <span>{this.requiresExtendedLrrpPages() ? 'Presumed/Positive path' : 'Standard path'}</span>
        </div>
        {this.state.errorMessage && (
          <div className={S.error} role="alert" aria-live="assertive">
            {this.state.errorMessage}
          </div>
        )}
        <div className={S.form}>
          {this.renderPageFields(currentPage)}
        </div>
        <div className={S.actions}>
          <Button label="Cancel" flat onClick={this.handleClose} disabled={this.state.loading} />
          {!atFirstPage && <Button label="Back" flat onClick={this.goToPreviousPage} disabled={this.state.loading} />}
          {!atLastPage && <Button label="Next" primary raised onClick={this.goToNextPage} disabled={this.state.loading} />}
          {atLastPage && <Button
            label={this.state.loading ? 'Saving...' : 'Create IFI LRRP'}
            primary
            raised
            onClick={this.submit}
            disabled={this.state.loading}
          />}
        </div>
      </Dialog>
    );
  }
}

IfiLrrpModal.propTypes = {
  active: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  // phase.type is expected to be one of: 'Assessment', 'Installation', or 'Workorder'.
  phase: PhaseType.isRequired,
  userName: PropTypes.string,
  userLeadCertNumber: PropTypes.string,
  ifiLrrpCreate: PropTypes.func.isRequired,
  updatePhaseTask: PropTypes.func.isRequired,
  taskSlug: PropTypes.string,
};

IfiLrrpModal.defaultProps = {
  userName: '',
  userLeadCertNumber: '',
  taskSlug: null,
};

export default connect(
  state => ({
    userName: state.user.name,
    userLeadCertNumber: state.user.lead_cert_number,
  }),
  { ifiLrrpCreate, updatePhaseTask },
)(IfiLrrpModal);



// WEBPACK FOOTER //
// ./client/app/screens/main/screens/phases/phaseShow/components/phaseActionMenu/ifiLrrpModal.jsx